/* eslint-disable @typescript-eslint/no-var-requires */
/* eslint-disable global-require */
/* eslint-disable no-nested-ternary */
import type { EducatorReviewListProps } from '@presentation/components';
import {
  Accordion,
  DetailListProPractical,
  DetailListSchedule,
  EducatorReviewList,
  HeaderProfile,
  PartnerInfoBox,
  RelatedLinks,
  Section,
  SectionSubtitle,
  SectionText,
  SectionTitle,
  SideLayoutContentWrapper,
  SideLayoutWrapper,
  Slideshow,
} from '@presentation/components';
import { useTranslation } from '@presentation/utils';
import type { FC } from 'react';
import { Suspense, lazy, useCallback, useMemo, useRef } from 'react';
import StickyBox from 'react-sticky-box';
import type { EducatorPageProps } from '..';
import { EducatorContactInfos } from './components/EducatorContactInfos';
import * as Styled from './styled';

export * from './types';

const Map = lazy(() =>
  import('../../components/Map').then((module) => ({ default: module.default })),
);

export const EducatorPage: FC<EducatorPageProps> = (props) => {
  const {
    slideshow,
    practicalInfo,
    addressCoordinates,
    header,
    reviewList,
    description,
    wellnessEngagement,
    educatorSchedule,
    onOverlayClick,
    showOverlay = false,
    extraDescription,
    relatedLinks,
    onSideCardClick,
    isTipawPartner,
    tipawPartnerRef,
  } = props;
  const { translate } = useTranslation();

  const reviewsRef = useRef<HTMLDivElement>(null);
  const showReviews = useMemo((): boolean => {
    if (reviewList?.loading) return true;
    return !!reviewList?.amount && reviewList?.average != null && !!reviewList.items?.length;
  }, [reviewList?.amount, reviewList?.average, reviewList?.items?.length, reviewList?.loading]);

  const onReviewsClick = useCallback(() => {
    reviewsRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);
  return (
    <>
      <HeaderProfile {...header} />
      <Styled.PageContainer>
        <SideLayoutWrapper>
          <SideLayoutContentWrapper>
            {slideshow?.items?.length ? <Slideshow {...slideshow} /> : <Styled.SpaceSeparator />}

            <Section>
              {description && (
                <>
                  <SectionTitle>{translate('proDetails.description.educator')}</SectionTitle>
                  <SectionText dangerouslySetInnerHTML={{ __html: description }} />
                </>
              )}
              {wellnessEngagement && (
                <>
                  <SectionSubtitle>{translate('glossary.engagement')}</SectionSubtitle>
                  <SectionText dangerouslySetInnerHTML={{ __html: wellnessEngagement }} />
                </>
              )}
            </Section>

            {!!practicalInfo && (
              <Section id="information-section">
                <SectionTitle>{translate('glossary.infoPratique')}</SectionTitle>
                <DetailListProPractical {...practicalInfo} />
              </Section>
            )}
            {addressCoordinates && (
              <Section>
                {addressCoordinates?.lat && addressCoordinates?.lng && !!Map && (
                  <Styled.MapContainer>
                    <Suspense fallback="...">
                      <Map
                        style={{ height: '400px' }}
                        center={{ ...addressCoordinates }}
                        zoom={12}
                      />
                    </Suspense>
                  </Styled.MapContainer>
                )}
              </Section>
            )}

            {extraDescription && (
              <SectionText dangerouslySetInnerHTML={{ __html: extraDescription }} />
            )}

            {educatorSchedule && (
              <Section>
                <SectionTitle>{translate('glossary.schedule')}</SectionTitle>
                <Accordion
                  defaultChecked
                  noScroll
                  tabs={
                    educatorSchedule
                      ? [
                          {
                            title: translate('glossary.bookingSchedule'),
                            content: <DetailListSchedule {...educatorSchedule} />,
                          },
                        ]
                      : []
                  }
                />
              </Section>
            )}
            {showReviews && (
              <Section id="card-review-list" ref={reviewsRef}>
                <SectionTitle>{translate('glossary.reviews')}</SectionTitle>
                <Section>
                  <EducatorReviewList {...(reviewList as EducatorReviewListProps)} />
                </Section>
              </Section>
            )}
          </SideLayoutContentWrapper>

          {showOverlay && <Styled.Overlay onClick={onOverlayClick} />}
          <Styled.StyledSideLayoutSideWrapper>
            <StickyBox offsetTop={85}>
              <EducatorContactInfos {...props} onReviewsClick={onReviewsClick} />
              {isTipawPartner && (
                <div ref={tipawPartnerRef}>
                  <PartnerInfoBox
                    title={translate('proDetails.partner.title')}
                    content={translate('proDetails.partner.content')}
                  />
                </div>
              )}
            </StickyBox>
          </Styled.StyledSideLayoutSideWrapper>
        </SideLayoutWrapper>
      </Styled.PageContainer>
      {relatedLinks && (
        <Styled.RelatedLinksContainer>
          <RelatedLinks {...relatedLinks} />
        </Styled.RelatedLinksContainer>
      )}
    </>
  );
};
